// Financial assessment — prototype content.
//
// Every question the production form asks (api/domain/care_form/seed →
// financial-status), re-cut as a one-question-per-screen flow with plainer
// copy. The 6th production section (Care needs) is intentionally out of scope:
// those are reused care-assessment questions, not financial ones.
//
// Copy is separated from the MUI rendering in FinancialAssessment.tsx. In the
// real app this would be served by the form builder; here it stands in for it.

// Known from her profile (set during onboarding) — stands in for data the real
// app already holds, so the name step confirms rather than asks cold.
export const PREFILL: Record<string, string> = {
  firstName: 'Margaret',
  lastName: 'Hughes',
};

// The {careRecipient} placeholder, app-wide — the recipient's first name.
export const RECIPIENT = PREFILL.firstName;

// ── Field kinds ──────────────────────────────────────────────────────────────
// How a step renders. The component owns the actual MUI for each.
export type FieldKind =
  | 'name' // two text fields: first + last
  | 'text'
  | 'date'
  | 'select' // dropdown (long option list, e.g. language)
  | 'choice' // tappable option cards (short option list)
  | 'currency' // £ amount
  | 'yesno' // segmented Yes / No, optionally + Not sure
  | 'multiselect' // toggleable chips, any number selected
  | 'table'; // a repeatable group of `columns`, one card per entry

export type Option = { value: string; label: string };

// Show this step only when an earlier answer is one of `in`. Declarative so
// the flow stays data-driven and inspect can read it as content, not logic.
export type ShowIf = { key: string; in: string[] };

export type Step = {
  key: string;
  section: SectionId;
  question: string;
  helper?: string;
  kind: FieldKind;
  options?: Option[];
  // Field label, shown inside the input. Every input carries one so the fields
  // are a consistent height (matching the two-field name step).
  label?: string;
  placeholder?: string;
  // currency: the period the amount covers, shown as a suffix (e.g. 'a year').
  per?: string;
  // currency: offer a period toggle (week / month / year). Marks the field as
  // an income, not a balance — the distinction users kept tripping on. `per`
  // is the default selection.
  periods?: Option[];
  // yesno: add a third 'Not sure' option for benefits people often can't recall.
  notSure?: boolean;
  // multiselect: render as a dropdown that shows the chosen options as chips
  // inside the field, rather than a wall of toggleable chips.
  asDropdown?: boolean;
  // table: the fields captured per entry (in a bottom sheet), the add-button
  // label, and the noun for the sheet title (Add / Edit <itemLabel>).
  columns?: Step[];
  addLabel?: string;
  itemLabel?: string;
  showIf?: ShowIf;
  // Progressive disclosure: follow-up fields that appear inline beneath this
  // question when the answer meets each reveal's `showIf` (which keys off this
  // step's answer). Keeps the step count fixed instead of pushing conditional
  // detail onto its own screen. Reveals are Steps too, so they render, format
  // and review exactly like top-level questions.
  reveals?: Step[];
};

// Period toggle for income amounts. The value doubles as the suffix shown in
// the field ('a week'), the label is the pill text ('Week').
export const PERIODS: Option[] = [
  { value: 'a week', label: 'Week' },
  { value: 'a month', label: 'Month' },
  { value: 'a year', label: 'Year' },
];

// ── Sections ─────────────────────────────────────────────────────────────────
export type SectionId =
  'about' | 'care-needs' | 'home' | 'income' | 'benefits' | 'carer-benefits';

export const SECTIONS: { id: SectionId; title: string }[] = [
  { id: 'about', title: `About ${RECIPIENT}` },
  { id: 'care-needs', title: 'Care needs' },
  { id: 'home', title: `${RECIPIENT}'s home` },
  { id: 'income', title: 'Income & savings' },
  { id: 'benefits', title: 'Benefits' },
  { id: 'carer-benefits', title: 'Carer benefits' },
];

// ── Intro ────────────────────────────────────────────────────────────────────
export const INTRO = {
  title: 'Financial status',
  description:
    'A few questions about your finances. Our care experts use your answers to build a funding guide made just for you.',
  points: [
    {
      icon: 'file-lines',
      text: `Questions about ${RECIPIENT}'s income, savings and home.`,
    },
    {
      icon: 'circle-check',
      text: 'A care expert reviews your answers and works out the funding options.',
    },
    {
      icon: 'hand-holding-heart',
      text: "We'll send a funding guide and help you apply for anything she qualifies for.",
    },
  ],
  footer:
    "Families we help often claim funding they didn't know existed. Rough figures are fine. You can change anything later.",
  cta: 'Start',
  time: 'Takes about 10 minutes',
};

// ── Complete ─────────────────────────────────────────────────────────────────
export const COMPLETE = {
  title: "That's everything",
  description:
    "You've completed the financial assessment. Book a call to discuss your funding guide with a care expert.",
  cta: 'Book a call',
};

// First-screen copy for when we don't already hold the recipient's name — they
// may not have added it at onboarding. The step asks for it rather than
// confirming a prefilled name.
export const NAME_UNKNOWN = {
  question: "Confirm the name of the person you're caring for",
  helper:
    "We don't have their name yet. Add it so we can tailor the assessment.",
};

// Common allergens, from the production care profile. `isCustom` is off in the
// prototype, so this is the full picking list.
const ALLERGENS: Option[] = [
  'Peanuts',
  'Almonds',
  'Walnuts',
  'Milk',
  'Eggs',
  'Shellfish',
  'Fish',
  'Wheat',
  'Soy',
  'Sesame seeds',
  'Pollen',
  'Dust mites',
  'Pet dander',
  'Mould spores',
  'Bee stings',
  'Wasp stings',
].map((l) => ({ value: l, label: l }));

// Care priorities, from the production preferences question.
const PRIORITIES: Option[] = [
  'Reduce amount of falls',
  'Be healthier',
  'Increase mobility and physical activity',
  'Enhance social interaction and reduce isolation',
  'Improve mental wellness and cognitive function',
  'Maintain or increase levels of independence',
  'Ensure adequate nutrition and hydration',
  'Effective medication management',
  'Reduce frequency of hospital visits or readmissions',
  'Effective pain management',
  'Maintain personal hygiene and grooming',
  'Engage in hobbies and personal interests',
  'Improve quality of sleep',
  'Live in comfort and a safe environment',
  'Remain in her own home for as long as possible',
  'Have autonomy in daily decisions and activities',
  'Clear communication with caregivers and healthcare professionals',
  'Increased involvement and support from family',
  'End-of-life preparations and discussions',
  'Advanced care planning and documentation',
].map((l) => ({ value: l, label: l }));

// How much support the recipient needs with medication, least to most.
const MEDICATION_SUPPORT: Option[] = [
  { value: 'none', label: 'No support required' },
  { value: 'reminding', label: 'Needs reminding' },
  { value: 'administering', label: 'Needs administering' },
];

// ── Steps ────────────────────────────────────────────────────────────────────
// Ordered. Conditional steps carry showIf and drop out when unmet.

export const STEPS: Step[] = [
  // ── About ──
  {
    key: 'name',
    section: 'about',
    question: `Can you confirm ${RECIPIENT}'s name?`,
    helper:
      "We've got this from her profile. Change it if anything's not right.",
    kind: 'name',
  },
  {
    key: 'dob',
    section: 'about',
    question: `What's ${RECIPIENT}'s date of birth?`,
    helper:
      'It helps us check which funding and benefits she qualifies for by age.',
    kind: 'date',
  },
  {
    key: 'postcode',
    section: 'about',
    question: `What's ${RECIPIENT}'s postcode?`,
    helper:
      'Funding and care vary by area, so we tailor things to where she lives.',
    kind: 'text',
    label: 'Postcode',
    placeholder: 'e.g. EH3 6HR',
  },

  // ── Care needs ──
  {
    key: 'allergies',
    section: 'care-needs',
    question: `Does ${RECIPIENT} have any allergies?`,
    kind: 'yesno',
    reveals: [
      {
        key: 'allergyList',
        section: 'care-needs',
        question: `List any allergies ${RECIPIENT} has`,
        kind: 'multiselect',
        options: ALLERGENS,
        showIf: { key: 'allergies', in: ['yes'] },
      },
    ],
  },
  {
    key: 'priorities',
    section: 'care-needs',
    question: `What's the most important thing to ${RECIPIENT}?`,
    kind: 'multiselect',
    asDropdown: true,
    label: 'Select all that apply',
    options: PRIORITIES,
  },
  {
    key: 'illnesses',
    section: 'care-needs',
    question: `Does ${RECIPIENT} have any illnesses or disabilities?`,
    kind: 'yesno',
    reveals: [
      {
        key: 'illnessList',
        section: 'care-needs',
        question: `What illnesses or disabilities does ${RECIPIENT} have?`,
        kind: 'table',
        addLabel: 'Add another',
        itemLabel: 'illness or disability',
        showIf: { key: 'illnesses', in: ['yes'] },
        columns: [
          {
            key: 'name',
            section: 'care-needs',
            question: 'Name of illness or disability',
            kind: 'text',
            label: 'Name of illness or disability',
            placeholder: 'e.g. Alzheimer’s, kidney failure, partially sighted',
          },
          {
            key: 'howLong',
            section: 'care-needs',
            question: `How long has ${RECIPIENT} had this?`,
            kind: 'text',
            label: 'How long',
            placeholder: 'e.g. two years, about 10 years',
          },
          {
            key: 'medicines',
            section: 'care-needs',
            question: 'Medicines or treatments prescribed',
            kind: 'text',
            label: 'Medicines or treatments',
            placeholder: 'e.g. Aricept, dialysis, or none',
          },
          {
            key: 'dosage',
            section: 'care-needs',
            question: 'Dosage and how often',
            kind: 'text',
            label: 'Dosage and frequency',
            placeholder: 'e.g. 10mg, one tablet a day, or none',
          },
          {
            key: 'support',
            section: 'care-needs',
            question: 'Support needed with taking medication',
            kind: 'select',
            label: 'Support with medication',
            options: MEDICATION_SUPPORT,
          },
        ],
      },
    ],
  },

  // ── Home ──
  {
    key: 'ownsHome',
    section: 'home',
    question: `Does ${RECIPIENT} own her home?`,
    helper:
      'Property can count towards a means test, so it shapes the funding picture.',
    kind: 'choice',
    options: [
      { value: 'outright', label: 'Yes, she owns it outright' },
      { value: 'joint-partner', label: 'Yes, jointly with a partner' },
      { value: 'joint-other', label: 'Yes, jointly with someone else' },
      { value: 'no', label: 'No, she rents or lives with family' },
    ],
    reveals: [
      {
        key: 'whoElse',
        section: 'home',
        question: 'Does anyone else live there?',
        helper:
          'Who lives with her can affect whether the home counts towards funding.',
        kind: 'choice',
        options: [
          { value: 'partner', label: 'Yes, her partner' },
          { value: 'joint-owner', label: 'Yes, a joint owner' },
          { value: 'dependent', label: 'Yes, a relative who depends on her' },
          { value: 'alone', label: 'No, she lives alone' },
        ],
        showIf: {
          key: 'ownsHome',
          in: ['outright', 'joint-partner', 'joint-other'],
        },
      },
      {
        key: 'homeValue',
        section: 'home',
        question: "Roughly what's the home worth?",
        helper: "A rough estimate is fine. We're not after an exact valuation.",
        kind: 'currency',
        label: 'Estimated value',
        showIf: {
          key: 'ownsHome',
          in: ['outright', 'joint-partner', 'joint-other'],
        },
      },
      {
        key: 'disregard',
        section: 'home',
        question: 'Is the home in a property disregard right now?',
        helper:
          "A property disregard is when the council agrees not to count the home's value towards care costs for a time. If you're not sure, that's fine.",
        kind: 'yesno',
        notSure: true,
        showIf: {
          key: 'ownsHome',
          in: ['outright', 'joint-partner', 'joint-other'],
        },
      },
      {
        key: 'deferred',
        section: 'home',
        question: 'Is there a deferred payment arrangement on the home?',
        helper:
          "That's a council scheme that lets care fees build up against the home's value rather than be paid now. Not sure? Leave it.",
        kind: 'yesno',
        notSure: true,
        showIf: {
          key: 'ownsHome',
          in: ['outright', 'joint-partner', 'joint-other'],
        },
      },
    ],
  },

  // ── Income & savings ──
  {
    key: 'savings',
    section: 'income',
    question: `How much does ${RECIPIENT} have in savings?`,
    helper:
      'The total in current accounts, savings accounts and ISAs. A rough figure is fine.',
    kind: 'currency',
    label: 'Total saved',
  },
  {
    key: 'assets',
    section: 'income',
    question: 'What are her other assets worth?',
    helper:
      'The total value, not how many she has. Things like investments, shares, premium bonds, or land and buildings.',
    kind: 'currency',
    label: 'Total value',
  },
  {
    key: 'statePension',
    section: 'income',
    question: `How much State Pension income does ${RECIPIENT} get?`,
    helper:
      'The regular payment she receives, not a savings pot. A letter from the Pension Service or a bank statement will show it.',
    kind: 'currency',
    label: 'Amount',
    per: 'a year',
    periods: PERIODS,
  },
  {
    key: 'privatePension',
    section: 'income',
    question: 'And how much private or workplace pension income?',
    helper:
      'The income she draws from a former employer or a personal pension, not the total pot.',
    kind: 'currency',
    label: 'Amount',
    per: 'a year',
    periods: PERIODS,
  },
  {
    key: 'otherIncome',
    section: 'income',
    question: `Does ${RECIPIENT} have any other income?`,
    helper:
      'Things like rent from a property, an annuity, or money from a trust.',
    kind: 'yesno',
    reveals: [
      {
        key: 'otherIncomeSource',
        section: 'income',
        question: 'Where does that income come from?',
        kind: 'text',
        label: 'Source',
        placeholder: 'e.g. rent from a flat',
        showIf: { key: 'otherIncome', in: ['yes'] },
      },
      {
        key: 'otherIncomeAmount',
        section: 'income',
        question: 'And roughly how much is that?',
        kind: 'currency',
        label: 'Amount',
        per: 'a year',
        periods: PERIODS,
        showIf: { key: 'otherIncome', in: ['yes'] },
      },
    ],
  },
  {
    key: 'receivingCare',
    section: 'income',
    question: `Is ${RECIPIENT} paying for any care at the moment?`,
    kind: 'yesno',
    reveals: [
      {
        key: 'careCost',
        section: 'income',
        question: "What's it costing?",
        helper: 'A rough figure is fine.',
        kind: 'currency',
        label: 'Amount',
        per: 'a month',
        periods: PERIODS,
        showIf: { key: 'receivingCare', in: ['yes'] },
      },
    ],
  },

  // ── Benefits ──
  {
    key: 'attendanceAllowance',
    section: 'benefits',
    question: `Is ${RECIPIENT} getting Attendance Allowance?`,
    helper:
      'Not sure what she gets? A letter from the DWP, or her bank statements, will show it.',
    kind: 'choice',
    options: [
      { value: 'higher', label: 'Yes, the higher rate' },
      { value: 'lower', label: 'Yes, the lower rate' },
      { value: 'yes-unsure', label: "Yes, but I'm not sure which rate" },
      { value: 'no', label: 'No' },
    ],
  },
  {
    key: 'pensionCredit',
    section: 'benefits',
    question: `Is ${RECIPIENT} getting Pension Credit?`,
    helper: 'A top-up for people over State Pension age on a lower income.',
    kind: 'yesno',
    notSure: true,
  },
  {
    key: 'pipDla',
    section: 'benefits',
    question: 'Is she getting PIP or Disability Living Allowance?',
    helper:
      'Personal Independence Payment (PIP) or DLA help with the extra costs of a long-term condition.',
    kind: 'yesno',
    notSure: true,
  },

  // ── Carer benefits ──
  {
    key: 'familyCaring35',
    section: 'carer-benefits',
    question: `Are you or a family member caring for ${RECIPIENT} 35+ hours a week?`,
    helper:
      'That includes everything from personal care to errands and managing appointments.',
    kind: 'yesno',
    reveals: [
      {
        key: 'carerEarnings',
        section: 'carer-benefits',
        question: 'Is that carer earning more than £196 a week?',
        helper:
          "Take-home pay, after tax. It affects whether they can claim Carer's Allowance.",
        kind: 'yesno',
        showIf: { key: 'familyCaring35', in: ['yes'] },
      },
    ],
  },
];
